import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { MonthYearPicker } from "@/components/ui/month-year-picker";
import { PROPERTY_SUBTYPES, shouldShowField } from "@/data/propertyFormConstants";

export const SpecificationsTab = ({
  formData,
  setFormData,
  handleChange,
  currentUserRole,
}) => {
  return (
<div className="space-y-6">
  {/* Property type indicator */}
  <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4">
    <p className="text-sm text-blue-800">
      <span className="font-medium">Showing fields for:</span>{' '}
      {formData.property_type.charAt(0).toUpperCase() + formData.property_type.slice(1)}
      {formData.property_subtype && ` → ${PROPERTY_SUBTYPES[formData.property_type]?.find(s => s.value === formData.property_subtype)?.label || formData.property_subtype}`}
    </p>
  </div>

  {/* Area Fields removed - already present in Basic Details tab (Step 3 Area Details) */}

  {/* Bedrooms, Bathrooms, Balconies - Residential only */}
  {(shouldShowField('bedrooms', formData.property_type, formData.property_subtype) ||
    shouldShowField('bathrooms', formData.property_type, formData.property_subtype) ||
    shouldShowField('balconies', formData.property_type, formData.property_subtype)) && (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {shouldShowField('bedrooms', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="bedrooms">Bedrooms</Label>
          <Input
            id="bedrooms"
            name="bedrooms"
            type="number"
            value={formData.bedrooms}
            onChange={handleChange}
            className="mt-1 h-11 rounded-sm"
            placeholder="3"
          />
        </div>
      )}

      {shouldShowField('bathrooms', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="bathrooms">Bathrooms</Label>
          <Input
            id="bathrooms"
            name="bathrooms"
            type="number"
            value={formData.bathrooms}
            onChange={handleChange}
            className="mt-1 h-11 rounded-sm"
            placeholder="2"
          />
        </div>
      )}

      {shouldShowField('balconies', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="balconies">Balconies</Label>
          <Input
            id="balconies"
            name="balconies"
            type="number"
            value={formData.balconies}
            onChange={handleChange}
            className="mt-1 h-11 rounded-sm"
            placeholder="2"
          />
        </div>
      )}
    </div>
  )}

  {/* Furnishing */}
  {shouldShowField('furnishing', formData.property_type, formData.property_subtype) && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <Label htmlFor="furnishing">Furnishing Status</Label>
        <select
          id="furnishing"
          name="furnishing"
          value={formData.furnishing}
          onChange={handleChange}
          className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
        >
          <option value="">Select Furnishing</option>
          <option value="furnished">Fully Furnished</option>
          <option value="semi_furnished">Semi-Furnished</option>
          <option value="unfurnished">Unfurnished</option>
          <option value="bare_shell">Bare Shell</option>
          <option value="warm_shell">Warm Shell</option>
        </select>
        {/* Furnishing type explanations */}
        {formData.furnishing && (
          <p className="text-xs text-slate-500 mt-1">
            {formData.furnishing === 'furnished' && 'Ready to move with all furniture & appliances'}
            {formData.furnishing === 'semi_furnished' && 'Basic fixtures installed (kitchen, wardrobes)'}
            {formData.furnishing === 'unfurnished' && 'Empty with basic flooring & painting'}
            {formData.furnishing === 'bare_shell' && 'Raw structure without any interior finishing'}
            {formData.furnishing === 'warm_shell' && 'Basic finishing done (flooring, ceiling, AC provision)'}
          </p>
        )}
      </div>

      {/* Show furnishing details only for furnished/semi-furnished */}
      {(formData.furnishing === 'furnished' || formData.furnishing === 'semi_furnished') && (
        <div>
          <Label htmlFor="furnishing_details">Furnishing Details</Label>
          <Input
            id="furnishing_details"
            name="furnishing_details"
            value={formData.furnishing_details}
            onChange={handleChange}
            className="mt-1 h-11 rounded-sm"
            placeholder="AC, Geyser, Modular Kitchen, Wardrobes"
          />
        </div>
      )}
    </div>
  )}

  {/* Parking */}
  {shouldShowField('parking', formData.property_type, formData.property_subtype) && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <Label htmlFor="parking">Parking Spaces</Label>
        <Input
          id="parking"
          name="parking"
          type="number"
          value={formData.parking}
          onChange={handleChange}
          className="mt-1 h-11 rounded-sm"
          placeholder="2"
        />
      </div>

      {shouldShowField('parking_type', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="parking_type">Parking Type</Label>
          <select
            id="parking_type"
            name="parking_type"
            value={formData.parking_type}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select Type</option>
            <option value="covered">Covered</option>
            <option value="open">Open</option>
            <option value="both">Both</option>
          </select>
        </div>
      )}
    </div>
  )}

  {/* Tower & Wing Information - Not shown for Land type */}
  {formData.property_type !== 'land' && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <Label htmlFor="tower">Tower Number / Name</Label>
        <Input
          id="tower"
          name="tower"
          value={formData.tower}
          onChange={handleChange}
          className="mt-1 h-11 rounded-sm"
          placeholder="Tower A / Tower 1"
          required={currentUserRole === 'sourcing_manager'}
          data-testid="tower-input"
        />
      </div>
      <div>
        <Label htmlFor="wing">Wing Number / Alphabet</Label>
        <Input
          id="wing"
          name="wing"
          value={formData.wing}
          onChange={handleChange}
          className="mt-1 h-11 rounded-sm"
          placeholder="Wing B / Wing 2"
          required={currentUserRole === 'sourcing_manager'}
          data-testid="wing-input"
        />
      </div>
    </div>
  )}

  {/* Floor Information - Floor Number hidden for Sourcing Managers */}
  {(shouldShowField('floor_number', formData.property_type, formData.property_subtype) ||
    shouldShowField('total_floors', formData.property_type, formData.property_subtype)) && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {currentUserRole !== 'sourcing_manager' && shouldShowField('floor_number', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="floor_number">Floor Number</Label>
          <Input
            id="floor_number"
            name="floor_number"
            type="number"
            value={formData.floor_number}
            onChange={handleChange}
            className="mt-1 h-11 rounded-sm"
            placeholder="5"
          />
        </div>
      )}

      {shouldShowField('total_floors', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="total_floors">Total Floors in Building</Label>
          <Input
            id="total_floors"
            name="total_floors"
            type="number"
            value={formData.total_floors}
            onChange={handleChange}
            className="mt-1 h-11 rounded-sm"
            placeholder="10"
          />
        </div>
      )}
    </div>
  )}

  {/* Direction & View */}
  {(shouldShowField('facing', formData.property_type, formData.property_subtype) ||
    shouldShowField('overlooking', formData.property_type, formData.property_subtype)) && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {shouldShowField('facing', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="facing">Facing Direction</Label>
          <select
            id="facing"
            name="facing"
            value={formData.facing}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select Direction</option>
            <option value="north">North</option>
            <option value="south">South</option>
            <option value="east">East</option>
            <option value="west">West</option>
            <option value="north_east">North-East</option>
            <option value="north_west">North-West</option>
            <option value="south_east">South-East</option>
            <option value="south_west">South-West</option>
          </select>
        </div>
      )}

      {shouldShowField('overlooking', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="overlooking">Overlooking</Label>
          <select
            id="overlooking"
            name="overlooking"
            value={formData.overlooking}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select View</option>
            <option value="park">Park</option>
            <option value="main_road">Main Road</option>
            <option value="garden">Garden</option>
            <option value="pool">Pool</option>
            <option value="club">Club</option>
          </select>
        </div>
      )}
    </div>
  )}

  {/* Age & Possession */}
  {(shouldShowField('age_of_property', formData.property_type, formData.property_subtype) ||
    shouldShowField('possession_status', formData.property_type, formData.property_subtype)) && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {shouldShowField('age_of_property', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="age_of_property">Age of Property</Label>
          <select
            id="age_of_property"
            name="age_of_property"
            value={formData.age_of_property}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select Age</option>
            <option value="under_construction">Under Construction</option>
            <option value="0-1">0-1 Years</option>
            <option value="1-5">1-5 Years</option>
            <option value="5-10">5-10 Years</option>
            <option value="10+">10+ Years</option>
          </select>
        </div>
      )}

      {shouldShowField('possession_status', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="possession_status">Possession Status</Label>
          <select
            id="possession_status"
            name="possession_status"
            value={formData.possession_status}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select Status</option>
            <option value="ready_to_move">Ready to Move</option>
            <option value="under_construction">Under Construction</option>
          </select>
        </div>
      )}
    </div>
  )}

  {formData.possession_status === 'under_construction' && (
    <div>
      <Label htmlFor="possession_date">Expected Possession Date</Label>
      <MonthYearPicker
        id="possession_date"
        name="possession_date"
        value={formData.possession_date}
        onChange={handleChange}
        className="mt-1"
      />
    </div>
  )}

  {/* Flooring & Water - Residential */}
  {(shouldShowField('flooring_type', formData.property_type, formData.property_subtype) ||
    shouldShowField('water_supply', formData.property_type, formData.property_subtype)) && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {shouldShowField('flooring_type', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="flooring_type">Flooring Type</Label>
          <select
            id="flooring_type"
            name="flooring_type"
            value={formData.flooring_type}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select Type</option>
            <option value="vitrified">Vitrified</option>
            <option value="marble">Marble</option>
            <option value="wooden">Wooden</option>
            <option value="granite">Granite</option>
            <option value="ceramic">Ceramic</option>
          </select>
        </div>
      )}

      {shouldShowField('water_supply', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="water_supply">Water Supply</Label>
          <select
            id="water_supply"
            name="water_supply"
            value={formData.water_supply}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select Type</option>
            <option value="municipal">Municipal</option>
            <option value="borewell">Borewell</option>
            <option value="both">Both</option>
          </select>
        </div>
      )}
    </div>
  )}

  {/* Power & Security */}
  {(shouldShowField('power_backup', formData.property_type, formData.property_subtype) ||
    shouldShowField('security', formData.property_type, formData.property_subtype)) && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {shouldShowField('power_backup', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="power_backup">Power Backup</Label>
          <select
            id="power_backup"
            name="power_backup"
            value={formData.power_backup}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select Option</option>
            <option value="full">Full</option>
            <option value="partial">Partial</option>
            <option value="none">None</option>
          </select>
        </div>
      )}

      {shouldShowField('security', formData.property_type, formData.property_subtype) && (
        <div>
          <Label htmlFor="security">Security</Label>
          <select
            id="security"
            name="security"
            value={formData.security}
            onChange={handleChange}
            className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">Select Type</option>
            <option value="24x7_security">24x7 Security</option>
            <option value="gated_community">Gated Community</option>
            <option value="both">Both</option>
          </select>
        </div>
      )}
    </div>
  )}

  {/* Lift Available */}
  {shouldShowField('lift_available', formData.property_type, formData.property_subtype) && (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="lift_available"
          name="lift_available"
          checked={formData.lift_available}
          onCheckedChange={(checked) => setFormData(prev => ({ 
            ...prev, 
            lift_available: checked,
            // Reset lift details if unchecked
            number_of_lifts: checked ? prev.number_of_lifts : "",
            lift_type: checked ? prev.lift_type : "",
            escalator_available: checked ? prev.escalator_available : false
          }))}
        />
        <Label htmlFor="lift_available" className="cursor-pointer">Lift Available</Label>
      </div>

      {/* Lift Details - Show when lift is available */}
      {formData.lift_available && (
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <h5 className="text-sm font-medium text-slate-700 mb-3">Lift Details</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Number of Lifts */}
            <div>
              <Label htmlFor="number_of_lifts">Number of Lifts</Label>
              <Input
                id="number_of_lifts"
                name="number_of_lifts"
                type="number"
                min="1"
                value={formData.number_of_lifts}
                onChange={handleChange}
                className="mt-1 h-10 rounded-sm"
                placeholder="2"
              />
            </div>

            {/* Lift Type */}
            <div>
              <Label htmlFor="lift_type">Lift Type</Label>
              <select
                id="lift_type"
                name="lift_type"
                value={formData.lift_type}
                onChange={handleChange}
                className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="">Select Type</option>
                <option value="passenger">Passenger Lift</option>
                <option value="service">Service Lift</option>
                <option value="goods">Goods Lift</option>
                <option value="passenger_service">Passenger + Service</option>
                <option value="all">All Types (Passenger, Service, Goods)</option>
              </select>
            </div>

            {/* Escalator - Only for Commercial */}
            {formData.property_type === 'commercial' && (
              <div className="flex items-center space-x-2 md:pt-7">
                <Checkbox
                  id="escalator_available"
                  checked={formData.escalator_available}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, escalator_available: checked }))}
                />
                <Label htmlFor="escalator_available" className="cursor-pointer text-sm">
                  Escalator Available
                </Label>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )}

  {/* ==================== COMMERCIAL/OFFICE SPECIFIC FIELDS ==================== */}
  {formData.property_type === 'commercial' && (
    <>
      {/* Office Configuration */}
      {shouldShowField('office_rooms', formData.property_type, formData.property_subtype) && (
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Office Configuration
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {shouldShowField('total_cabins', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="total_cabins">Total Cabins</Label>
                <Input
                  id="total_cabins"
                  name="total_cabins"
                  type="number"
                  value={formData.total_cabins}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="10"
                />
              </div>
            )}
            {shouldShowField('workstations', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="workstations">Workstations</Label>
                <Input
                  id="workstations"
                  name="workstations"
                  type="number"
                  value={formData.workstations}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="50"
                />
              </div>
            )}
            {shouldShowField('washrooms', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="washrooms">Washrooms</Label>
                <Input
                  id="washrooms"
                  name="washrooms"
                  type="number"
                  value={formData.washrooms}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="4"
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            {shouldShowField('meeting_rooms', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="meeting_rooms">Meeting Rooms</Label>
                <Input
                  id="meeting_rooms"
                  name="meeting_rooms"
                  type="number"
                  value={formData.meeting_rooms}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="3"
                />
              </div>
            )}
            {shouldShowField('conference_rooms', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="conference_rooms">Conference Rooms</Label>
                <Input
                  id="conference_rooms"
                  name="conference_rooms"
                  type="number"
                  value={formData.conference_rooms}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="2"
                />
              </div>
            )}
            {shouldShowField('total_seats', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="total_seats">Total Seats</Label>
                <Input
                  id="total_seats"
                  name="total_seats"
                  type="number"
                  value={formData.total_seats}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="100"
                />
              </div>
            )}
          </div>
          {shouldShowField('server_room', formData.property_type, formData.property_subtype) && (
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="server_room"
                name="server_room"
                checked={formData.server_room}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, server_room: checked }))}
              />
              <Label htmlFor="server_room" className="cursor-pointer">Server Room Available</Label>
            </div>
          )}
        </div>
      )}

      {/* Shop/Showroom Specific */}
      {(shouldShowField('frontage_width', formData.property_type, formData.property_subtype) ||
        shouldShowField('ceiling_height', formData.property_type, formData.property_subtype)) && (
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Shop/Showroom Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {shouldShowField('frontage_width', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="frontage_width">Frontage Width (ft)</Label>
                <Input
                  id="frontage_width"
                  name="frontage_width"
                  value={formData.frontage_width}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="20"
                />
              </div>
            )}
            {shouldShowField('ceiling_height', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="ceiling_height">Ceiling Height (ft)</Label>
                <Input
                  id="ceiling_height"
                  name="ceiling_height"
                  value={formData.ceiling_height}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="12"
                />
              </div>
            )}
            {shouldShowField('display_windows', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="display_windows">Display Windows</Label>
                <Input
                  id="display_windows"
                  name="display_windows"
                  type="number"
                  value={formData.display_windows}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="3"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Warehouse/Industrial Specific */}
      {(shouldShowField('loading_docks', formData.property_type, formData.property_subtype) ||
        shouldShowField('floor_strength', formData.property_type, formData.property_subtype)) && (
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Warehouse/Industrial Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {shouldShowField('loading_docks', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="loading_docks">Loading Docks</Label>
                <Input
                  id="loading_docks"
                  name="loading_docks"
                  type="number"
                  value={formData.loading_docks}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="4"
                />
              </div>
            )}
            {shouldShowField('floor_strength', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="floor_strength">Floor Strength (kg/sq ft)</Label>
                <Input
                  id="floor_strength"
                  name="floor_strength"
                  value={formData.floor_strength}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="500"
                />
              </div>
            )}
          </div>
          {shouldShowField('crane_facility', formData.property_type, formData.property_subtype) && (
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="crane_facility"
                name="crane_facility"
                checked={formData.crane_facility}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, crane_facility: checked }))}
              />
              <Label htmlFor="crane_facility" className="cursor-pointer">Crane Facility Available</Label>
            </div>
          )}
        </div>
      )}

      {/* Hotel/Restaurant Specific */}
      {(shouldShowField('total_rooms', formData.property_type, formData.property_subtype) ||
        shouldShowField('seating_capacity', formData.property_type, formData.property_subtype)) && (
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Hospitality Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {shouldShowField('total_rooms', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="total_rooms">Total Rooms</Label>
                <Input
                  id="total_rooms"
                  name="total_rooms"
                  type="number"
                  value={formData.total_rooms}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="50"
                />
              </div>
            )}
            {shouldShowField('banquet_halls', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="banquet_halls">Banquet Halls</Label>
                <Input
                  id="banquet_halls"
                  name="banquet_halls"
                  type="number"
                  value={formData.banquet_halls}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="2"
                />
              </div>
            )}
            {shouldShowField('restaurant_area', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="restaurant_area">Restaurant Area (sq ft)</Label>
                <Input
                  id="restaurant_area"
                  name="restaurant_area"
                  value={formData.restaurant_area}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="2000"
                />
              </div>
            )}
            {shouldShowField('seating_capacity', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="seating_capacity">Seating Capacity</Label>
                <Input
                  id="seating_capacity"
                  name="seating_capacity"
                  type="number"
                  value={formData.seating_capacity}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="100"
                />
              </div>
            )}
            {shouldShowField('kitchen_area', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="kitchen_area">Kitchen Area (sq ft)</Label>
                <Input
                  id="kitchen_area"
                  name="kitchen_area"
                  value={formData.kitchen_area}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="500"
                />
              </div>
            )}
          </div>
          {shouldShowField('outdoor_seating', formData.property_type, formData.property_subtype) && (
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="outdoor_seating"
                name="outdoor_seating"
                checked={formData.outdoor_seating}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, outdoor_seating: checked }))}
              />
              <Label htmlFor="outdoor_seating" className="cursor-pointer">Outdoor Seating Available</Label>
            </div>
          )}
        </div>
      )}
    </>
  )}

  {/* ==================== LAND SPECIFIC FIELDS ==================== */}
  {formData.property_type === 'land' && (
    <>
      {/* Plot Dimensions */}
      {(shouldShowField('plot_length', formData.property_type, formData.property_subtype) ||
        shouldShowField('plot_width', formData.property_type, formData.property_subtype)) && (
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Plot Dimensions
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="plot_length">Plot Length (ft)</Label>
              <Input
                id="plot_length"
                name="plot_length"
                value={formData.plot_length}
                onChange={handleChange}
                className="mt-1 h-11 rounded-sm"
                placeholder="100"
              />
            </div>
            <div>
              <Label htmlFor="plot_width">Plot Width (ft)</Label>
              <Input
                id="plot_width"
                name="plot_width"
                value={formData.plot_width}
                onChange={handleChange}
                className="mt-1 h-11 rounded-sm"
                placeholder="50"
              />
            </div>
            {shouldShowField('road_width', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="road_width">Road Width (ft)</Label>
                <Input
                  id="road_width"
                  name="road_width"
                  value={formData.road_width}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="30"
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-6 mt-4">
            {shouldShowField('corner_plot', formData.property_type, formData.property_subtype) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="corner_plot"
                  name="corner_plot"
                  checked={formData.corner_plot}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, corner_plot: checked }))}
                />
                <Label htmlFor="corner_plot" className="cursor-pointer">Corner Plot</Label>
              </div>
            )}
            {shouldShowField('boundary_wall', formData.property_type, formData.property_subtype) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="boundary_wall"
                  name="boundary_wall"
                  checked={formData.boundary_wall}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, boundary_wall: checked }))}
                />
                <Label htmlFor="boundary_wall" className="cursor-pointer">Boundary Wall</Label>
              </div>
            )}
            {shouldShowField('gated_community', formData.property_type, formData.property_subtype) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="gated_community"
                  name="gated_community"
                  checked={formData.gated_community}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, gated_community: checked }))}
                />
                <Label htmlFor="gated_community" className="cursor-pointer">Gated Community</Label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Commercial/Industrial Plot Details */}
      {(shouldShowField('fsi_available', formData.property_type, formData.property_subtype) ||
        shouldShowField('commercial_zone', formData.property_type, formData.property_subtype) ||
        shouldShowField('industrial_zone', formData.property_type, formData.property_subtype) ||
        shouldShowField('residential_zone', formData.property_type, formData.property_subtype)) && (
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h4 className="text-lg font-semibold text-[#1e3a5f] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Zoning & Approvals
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {shouldShowField('fsi_available', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="fsi_available">FSI Available</Label>
                <Input
                  id="fsi_available"
                  name="fsi_available"
                  value={formData.fsi_available}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="2.5"
                />
              </div>
            )}
            {shouldShowField('land_use_permission', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="land_use_permission">Land Use Permission</Label>
                <Input
                  id="land_use_permission"
                  name="land_use_permission"
                  value={formData.land_use_permission}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="Educational, Commercial, etc."
                />
              </div>
            )}
          </div>
          
          {/* All Zone Types */}
          <div className="mt-4">
            <Label className="text-sm font-semibold text-[#1e3a5f] mb-3 block">Zone Classification</Label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {shouldShowField('residential_zone', formData.property_type, formData.property_subtype) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="residential_zone"
                    name="residential_zone"
                    checked={formData.residential_zone}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, residential_zone: checked }))}
                  />
                  <Label htmlFor="residential_zone" className="cursor-pointer text-sm">Residential Zone</Label>
                </div>
              )}
              {shouldShowField('commercial_zone', formData.property_type, formData.property_subtype) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="commercial_zone"
                    name="commercial_zone"
                    checked={formData.commercial_zone}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, commercial_zone: checked }))}
                  />
                  <Label htmlFor="commercial_zone" className="cursor-pointer text-sm">Commercial Zone</Label>
                </div>
              )}
              {shouldShowField('industrial_zone', formData.property_type, formData.property_subtype) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="industrial_zone"
                    name="industrial_zone"
                    checked={formData.industrial_zone}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, industrial_zone: checked }))}
                  />
                  <Label htmlFor="industrial_zone" className="cursor-pointer text-sm">Industrial Zone</Label>
                </div>
              )}
              {shouldShowField('agricultural_zone', formData.property_type, formData.property_subtype) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="agricultural_zone"
                    name="agricultural_zone"
                    checked={formData.agricultural_zone}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agricultural_zone: checked }))}
                  />
                  <Label htmlFor="agricultural_zone" className="cursor-pointer text-sm">Agricultural Zone</Label>
                </div>
              )}
              {shouldShowField('mixed_use_zone', formData.property_type, formData.property_subtype) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="mixed_use_zone"
                    name="mixed_use_zone"
                    checked={formData.mixed_use_zone}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, mixed_use_zone: checked }))}
                  />
                  <Label htmlFor="mixed_use_zone" className="cursor-pointer text-sm">Mixed Use Zone</Label>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-6 mt-4">
            {shouldShowField('pollution_clearance', formData.property_type, formData.property_subtype) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="pollution_clearance"
                  name="pollution_clearance"
                  checked={formData.pollution_clearance}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, pollution_clearance: checked }))}
                />
                <Label htmlFor="pollution_clearance" className="cursor-pointer">Pollution Clearance</Label>
              </div>
            )}
            {shouldShowField('na_order_available', formData.property_type, formData.property_subtype) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="na_order_available"
                  name="na_order_available"
                  checked={formData.na_order_available}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, na_order_available: checked }))}
                />
                <Label htmlFor="na_order_available" className="cursor-pointer">NA Order Available</Label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agricultural/Farm Land Details */}
      {(shouldShowField('soil_type', formData.property_type, formData.property_subtype) ||
        shouldShowField('water_source', formData.property_type, formData.property_subtype)) && (
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Agricultural Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {shouldShowField('soil_type', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="soil_type">Soil Type</Label>
                <select
                  id="soil_type"
                  name="soil_type"
                  value={formData.soil_type}
                  onChange={handleChange}
                  className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Select Soil Type</option>
                  <option value="black">Black (Cotton)</option>
                  <option value="red">Red</option>
                  <option value="alluvial">Alluvial</option>
                  <option value="laterite">Laterite</option>
                  <option value="sandy">Sandy</option>
                  <option value="loamy">Loamy</option>
                </select>
              </div>
            )}
            {shouldShowField('water_source', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="water_source">Water Source</Label>
                <select
                  id="water_source"
                  name="water_source"
                  value={formData.water_source}
                  onChange={handleChange}
                  className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Select Water Source</option>
                  <option value="canal">Canal</option>
                  <option value="well">Well</option>
                  <option value="borewell">Borewell</option>
                  <option value="river">River</option>
                  <option value="rainwater">Rainwater</option>
                  <option value="none">None</option>
                </select>
              </div>
            )}
            {shouldShowField('irrigation_facility', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="irrigation_facility">Irrigation Facility</Label>
                <select
                  id="irrigation_facility"
                  name="irrigation_facility"
                  value={formData.irrigation_facility}
                  onChange={handleChange}
                  className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Select Irrigation</option>
                  <option value="drip">Drip Irrigation</option>
                  <option value="sprinkler">Sprinkler</option>
                  <option value="flood">Flood Irrigation</option>
                  <option value="none">None</option>
                </select>
              </div>
            )}
            {shouldShowField('road_access', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="road_access">Road Access</Label>
                <select
                  id="road_access"
                  name="road_access"
                  value={formData.road_access}
                  onChange={handleChange}
                  className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Select Access</option>
                  <option value="paved">Paved Road</option>
                  <option value="tar">Tar Road</option>
                  <option value="gravel">Gravel Road</option>
                  <option value="mud">Mud Road</option>
                  <option value="none">No Direct Access</option>
                </select>
              </div>
            )}
            {shouldShowField('existing_crops', formData.property_type, formData.property_subtype) && (
              <div>
                <Label htmlFor="existing_crops">Existing Crops</Label>
                <Input
                  id="existing_crops"
                  name="existing_crops"
                  value={formData.existing_crops}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="Sugarcane, Cotton, etc."
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-6 mt-4">
            {shouldShowField('electricity_available', formData.property_type, formData.property_subtype) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="electricity_available"
                  name="electricity_available"
                  checked={formData.electricity_available}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, electricity_available: checked }))}
                />
                <Label htmlFor="electricity_available" className="cursor-pointer">Electricity Available</Label>
              </div>
            )}
            {shouldShowField('bore_well', formData.property_type, formData.property_subtype) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="bore_well"
                  name="bore_well"
                  checked={formData.bore_well}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, bore_well: checked }))}
                />
                <Label htmlFor="bore_well" className="cursor-pointer">Bore Well</Label>
              </div>
            )}
            {shouldShowField('farm_house_allowed', formData.property_type, formData.property_subtype) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="farm_house_allowed"
                  name="farm_house_allowed"
                  checked={formData.farm_house_allowed}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, farm_house_allowed: checked }))}
                />
                <Label htmlFor="farm_house_allowed" className="cursor-pointer">Farm House Allowed</Label>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )}
</div>
  );
};
